import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";

import translate from "../../../../../i18n/translate";
import ApiRequest from "../../../../../actions/ApiRequest";
import Toast from "../../../../common/Toast";

// Runs the Azy product update and shows it happening. The update signs in
// to the products site on the server, reads every product and writes it to
// the catalogue; that takes a minute or two, so it runs in the background
// and this page polls its progress every two seconds.
const POLL_MS = 2000;

const STAGES = {
  open: "Opening the products site",
  login: "Signing in",
  wait: "Waiting for the product list",
  read: "Reading products",
  write: "Writing products",
};

const formatElapsed = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
};

const AzyUpdateProducts = ({ isDarktheme }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const readStatus = async () => {
    try {
      const res = await ApiRequest.post("update_azy_bundles?operation=status");
      if (res?.data?.status === "success") setJob(res.data.job);
    } catch (err) {
      // the axios interceptor already reports the failure
    }
  };

  useEffect(() => {
    readStatus();
    return () => clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (job?.running) timer.current = setTimeout(readStatus, POLL_MS);
  }, [job]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await ApiRequest.post("update_azy_bundles?operation=start");
      if (res?.data?.status === "success") {
        setJob(res.data.job);
        if (!res.data.started) Toast.fire({ title: res.data.reason, icon: "info" });
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setStarting(false);
    }
  };

  const running = Boolean(job?.running);
  const finished = job && !job.running && job.finished;
  const counted = running && (job.stage === "read" || job.stage === "write") && job.total > 0;
  const percent = counted ? Math.round((job.done / job.total) * 100) : 0;
  const result = job?.result || {};

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("azyUpdateHint")}
      </Typography>

      <Paper sx={{ padding: 3, maxWidth: 720 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Button variant="contained" onClick={start} disabled={starting || running}>
            {starting ? <CircularProgress size={20} color="inherit" /> : translate("Update Products")}
          </Button>
          {running && (
            <Chip
              size="small"
              color="primary"
              label={<>{translate("Updating")} · {formatElapsed(job.elapsed_seconds)}</>}
            />
          )}
          {finished && (
            <Chip
              size="small"
              color={job.ok ? "success" : "error"}
              label={<>{translate("Last run")}: {job.finished}</>}
            />
          )}
        </Box>

        {running && (
          <Box sx={{ marginTop: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 0.5 }}>
              <Typography variant="body2">
                {translate(STAGES[job.stage] || "Starting")}
                {counted && ` (${job.done} / ${job.total})`}
              </Typography>
              {counted && <Typography variant="body2">{percent}%</Typography>}
            </Box>
            <LinearProgress variant={counted ? "determinate" : "indeterminate"} value={percent} />
          </Box>
        )}

        {finished && (
          <Alert severity={job.ok ? "success" : "error"} sx={{ marginTop: 3 }}>
            {job.ok ? (
              <>
                {result.total_scraped} {translate("products read")}, {result.inserted} {translate("new")},{" "}
                {result.updated} {translate("updated")}, {formatElapsed(job.elapsed_seconds)}.
              </>
            ) : (
              job.error || translate("The update did not complete.")
            )}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps, null)(AzyUpdateProducts);
